import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { fetchGroups } from "store/groupsSlice";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { en, de } from 'yup-locales';
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import * as Sentry from "@sentry/react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "helpers/toast";
import { helpTextIcon } from "helpers/helpText";
import ErrorAlert from "components/ui/ErrorAlert";
import Creatable from 'react-select/creatable';
import { useTranslation } from "translation/useTranslation";
import { getLang } from "store/userSlice";
import { Button, Field, Input, Select } from "components/ui";

export default function GroupForm({ id, selectedGroup, setSelectedGroup, handleShowDelete, categoryOptions }) {
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const { helps } = useOutletContext();

  const { translate, editableTranslate } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const group = useMemo(() => {
    if (!selectedGroup || selectedGroup === '-') return null;
    return selectedGroup;
  }, [selectedGroup]);

  const categoryHelp = useMemo(() => {
    if (helps) {
      return helps.filter(h => h.key === 'category')[0] || null;
    }
    return null;
  }, [helps]);

  const schema = yup.object({
    category: yup.object().shape({
      value: yup.string().notRequired().max(255),
      label: yup.string().notRequired().max(255)
    }).notRequired().label(translate('group_setting.category')),
    name: yup.string().required().min(4).max(50).label(translate('group_setting.name')),
  });

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (group) {
      reset({
        category: group.category ? {
          label: group.category,
          value: group.category
        } : null,
        name: group.name
      });
    } else {
      reset({
        category: null,
        name: ''
      });
    }
  }, [group]);

  const onSubmit = async (data) => {
    setError(null);
    setLoading(true);

    const formData = {
      category: data.category?.value || '',
      name: data.name,
    };

    try {
      let res;
      if (id) {
        res = await api.put(`/groups/${id}`, formData);
      } else {
        res = await api.post('/groups', formData);
      }
      dispatch(fetchGroups());

      if (!id) {
        setSelectedGroup(null);
      }
      toast.success(translate('form.success.saved'));
    } catch (err) {
      setError(errorMessage(err, 'save group'));
      Sentry.captureException(err);
    }
    setLoading(false);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-3 rounded-sm">
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h4>{editableTranslate('group_setting.group')}</h4>
          <p className="mb-0">{editableTranslate('group_setting.group_description')}</p>
        </div>
        <div>
          {
            id && <Button variant="secondary" onClick={() => handleShowDelete(group)} loading={loading} data-testid="delete-button" className="mr-2">
              <span className="bi bi-trash"></span> {editableTranslate('global.delete')}
            </Button>
          }
          <Button type="submit" variant="primary" loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
        </div>
      </div>
      {
        error && <div>
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </div>
      }

      <div className="form-row">
        <div className="form-group col-6">
          <Field label={<>{editableTranslate('group_setting.category')} {categoryHelp ? helpTextIcon(categoryHelp?.key, categoryHelp?.short_text) : ''}</>} error={errors?.category?.value?.message}>
            <Controller
              name="category"
              control={control}
              render={({ field }) => <div style={{ display: 'contents' }} data-testid="category-select">
                <Select as={Creatable} {...field} placeholder={translate('form.select_placeholder')} formatCreateLabel={val => translate('form.createable_label') + ` "${val}"`} isClearable options={categoryOptions} error={!!errors?.category} noOptionsMessage={() => translate('form.no_options_createable')} />
              </div>
              } />
          </Field>
        </div>

        <div className="form-group col-6">
          <Field label={<>{editableTranslate('group_setting.name')} {categoryHelp && <i className="bi bi-info-circle invisible"></i>}</>} error={errors?.name?.message}>
            <Input type="text" error={!!errors?.name} {...register('name')} maxLength={50} data-testid="name-input" />
          </Field>
        </div>
      </div>
    </form>
  );
}
